import produce from "immer";
import create from "zustand";
import { persist } from "zustand/middleware";

export const validColor = "#32c48d";
export const invalidColor = "#E86161";

export enum WitnessesVisualizationKey {
  none = "none",
  witnessedBy = "witnessedBy",
  witnessedOthers = "witnessedOthers",
  bidirectional = "bidirectional"
}

export enum WitnessesVisualizationVariant {
  all = "all",
  valid = "valid",
  invalid = "invalid"
}

export enum VisualizationLayer {
  problematicHexes = "problematicHexes",
  tooCloseToWitness = "tooCloseToWitness",
  witnessLines = "witnessLines",
  elevationContours = "elevationContours",
  hotspots = "hotspots",
  locations = "locations",
  satellite = "satellite",
  cellPoints = "cellPoints",
}

export enum DistanceUnit {
  m = "m",
  ft = "ft"
}

export const relativePerformanceMetrics = [
  {
    key: "status",
    title: "online status"
  },
  {
    key: "verified",
    title: "wallet verification status"
  },
  {
    key: "reward_scale",
    title: "simulated transmit scale" // TODO: add current transmit scale separately
  },
  {
    key: "rx_reward_unit",
    title: "witness reward units"
  },
  {
    key: "rewardsSummary.day",
    title: "rewards for the past 24h"
  },
  {
    key: "rewardsSummary.dayWeekAverage",
    title: "daily rewards (7d avg)"
  },
  {
    key: "rewardsSummary.month",
    title: "monthly rewards"
  },
  {
    key: "witnessesSummary.witnessedBy.valid",
    title: "number of valid witnesses"
  },
  {
    key: "witnessesSummary.witnessedOthers.valid",
    title: "number of valid witnessed others"
  },
  {
    key: "witnessesSummary.bidirectional.valid",
    title: "number of valid bidirectional witnesses"
  }
];

type MapConfig = {
  witnessesVisualizationKey: WitnessesVisualizationKey;
  setWitnessesVisualizationKey: (
    newVisualizationKey: WitnessesVisualizationKey
  ) => void;
  witnessesVisualizationVariant: WitnessesVisualizationVariant;
  setWitnessesVisualizationVariant: (
    newVisualizationVariant: WitnessesVisualizationVariant
  ) => void;
  visibleLayers: { [layer in keyof typeof VisualizationLayer]: boolean };
  toggleVisibleLayer: (layer: VisualizationLayer, value?: boolean) => void;
  pointColorMetricIteratee: string;
  setPointColorMetricIteratee: (newMetric: string) => void;
  showRelativePerformance: boolean; // This is here because we only fetch the witnesses and rewards for all hotspots nearby if either this or pointColorMetricIteratee requires it. Therefore the HotspotProvider needs access to this variable, which wasn't possible when it was in the FilteredListProvider
  setShowRelativePerformance: (newValue: boolean) => void;
  distanceUnit: DistanceUnit;
  setDistanceUnit: (newValue: DistanceUnit) => void;
  getAppearanceDistanceValue: (
    distanceInMeters: number,
    useKmAndMiles: boolean
  ) => number;
  getDistanceValueInMeters: (appearanceDistaceValue: number) => number;
  renderDistance: (distanceInMeters: number, useKmAndMiles: boolean) => string;
};

const useMapConfig = create<MapConfig>(
  persist(
    (set, get) => ({
      witnessesVisualizationKey: WitnessesVisualizationKey.none,
      setWitnessesVisualizationKey: (newVisualizationKey) =>
        set({ witnessesVisualizationKey: newVisualizationKey }),
      witnessesVisualizationVariant: WitnessesVisualizationVariant.all,
      setWitnessesVisualizationVariant: (newVisualizationVariant) =>
        set({ witnessesVisualizationVariant: newVisualizationVariant }),
      visibleLayers: {
        [VisualizationLayer.tooCloseToWitness]: false,
        [VisualizationLayer.witnessLines]: true,
        [VisualizationLayer.problematicHexes]: false,
        [VisualizationLayer.elevationContours]: false,
        [VisualizationLayer.hotspots]: true,
        [VisualizationLayer.locations]: false,
        [VisualizationLayer.satellite]: false,
        [VisualizationLayer.cellPoints]: false
      },
      toggleVisibleLayer: (layer, value) =>
        set((state) =>
          produce(state, (draft) => {
            if (value !== undefined) {
              draft.visibleLayers[layer] = value;

              return;
            }

            draft.visibleLayers[layer] = !draft.visibleLayers[layer];
          })
        ),
      pointColorMetricIteratee: relativePerformanceMetrics[0].key,
      setPointColorMetricIteratee: (newMetric) =>
        set({ pointColorMetricIteratee: newMetric }),
      showRelativePerformance: false,
      setShowRelativePerformance: (newValue) =>
        set({ showRelativePerformance: newValue }),
      distanceUnit: DistanceUnit.m,
      setDistanceUnit: (newValue) => set({ distanceUnit: newValue }),
      getAppearanceDistanceValue: (distanceInMeters, useKmAndMiles = false) => {
        const distanceUnit = get().distanceUnit;

        switch (distanceUnit) {
          case DistanceUnit.ft:
            return useKmAndMiles
              ? Math.round(distanceInMeters * 0.000621371 * 100) / 100
              : Math.round(distanceInMeters * 3.28084);
          case DistanceUnit.m:
            return useKmAndMiles
              ? Math.round(distanceInMeters * 0.001 * 100) / 100
              : distanceInMeters;
        }
      },
      getDistanceValueInMeters: (appearanceDistaceValue) => {
        const distanceUnit = get().distanceUnit;

        return distanceUnit === DistanceUnit.ft
          ? appearanceDistaceValue / 3.28084
          : appearanceDistaceValue;
      },
      renderDistance: (distanceInMeters, useKmAndMiles = false) => {
        let unit;

        switch (get().distanceUnit) {
          case DistanceUnit.ft:
            unit = useKmAndMiles ? "mi" : DistanceUnit.ft;
            break;
          case DistanceUnit.m:
            unit = useKmAndMiles ? "km" : DistanceUnit.m;
        }

        const digits = useKmAndMiles ? 1 : 0;

        return `${get()
          .getAppearanceDistanceValue(distanceInMeters, useKmAndMiles)
          .toLocaleString(undefined, {
            minimumFractionDigits: digits,
            maximumFractionDigits: digits
          })}${unit}`;
      }
    }),
    {
      name: "map_config",
      version: 9
    }
  )
);

export default useMapConfig;
